import { gameLoop } from "appworks/core/game-loop";
import { Services } from "appworks/services/services";
import { SoundEvent } from "appworks/services/sound/sound-events";
import { Contract } from "appworks/utils/contracts/contract";
import { Point } from "appworks/utils/geom/point";
import { RandomFromArray, RandomRangeInt } from "appworks/utils/math/random";
import { Timer } from "appworks/utils/timer";
import { spine } from "pixi.js";
import { Signal } from "signals";
import { canvasManager } from "../canvas/canvas-manager";
import { Orientation } from "../canvas/orientation";
import { graphicsManager } from "../graphics-manager";
import { Container } from "./container";
import { DualPosition } from "./dual-position";
import { Position } from "./position";
import { RenderOrientation } from "./render-orientation";

export class SpineContainer extends Container {

    public onStart: Signal = new Signal();
    public onComplete: Signal = new Signal();
    public onEvent: Signal = new Signal();

    private attachments: Map<string, string> = new Map();

    private spineId: string;
    private spines: { landscape?: spine.Spine, portrait?: spine.Spine } = {};

    private playing: boolean = false;
    private looping: boolean = false;
    private playContract?: Contract<void>;
    private animation?: spine.core.Animation;

    private spinePosition: DualPosition;

    private playTimeout?: number;

    private initialSpineSetup: boolean;

    private spineVisible: boolean = false;

    constructor(spineId: string, position: DualPosition) {
        super();

        this.spineId = spineId;
        this.name = spineId + "_" + RandomRangeInt(0, 100);
        this.spinePosition = position;
        this.initialSpineSetup = true;
        this.spineVisible = false;

        canvasManager.onOrientationChange.add(() => this.updateSpineVisibility());
    }

    public updateTransform(): void {
        super.updateTransform();
        this.setupSpine();
    }

    public playOnce(animation?: number | string | spine.core.Animation, stopAtEnd: boolean = false, timeScale = 1): Contract<void> {
        if (!animation) {
            animation = this.getAnimations()[0];
        }

        switch (typeof (animation)) {
            case "string":
                animation = this.getFirstSpine().spineData.animations.find((x) => x.name === animation);
                break;
            case "number":
                if (animation >= 0 && animation < this.getAnimations().length) {
                    animation = this.getAnimations()[animation];
                } else {
                    throw new Error(`Invalid animation index given for ${this.name} (${animation})`);
                }
                break;
        }

        if (this.playing) {
            this.stop();
        }

        this.animation = animation;

        const duration = (animation.duration * 1000) / timeScale;

        const contract: Contract<void> = new Contract((resolve, cancel) => {
            this.playTimeout = Timer.setTimeout(() => {
                if (this.playTimeout) {
                    Timer.clearTimeout(this.playTimeout);
                    this.playTimeout = undefined;
                }
                this.playContract = undefined;
                if (stopAtEnd) {
                    this.stop();
                }
                this.onComplete.dispatch();
                resolve();
            }, duration);

            cancel(() => {
                if (this.playTimeout) {
                    Timer.clearTimeout(this.playTimeout);
                    this.playTimeout = undefined;
                }
                if (!this._destroyed && stopAtEnd) {
                    this.stop();
                }
            });

            this.setupPlay(false, timeScale);
        });

        this.playContract = contract;
        return this.playContract;
    }

    public play(animation?: number | string | spine.core.Animation, timeScale = 1): void {
        // TODO: Refactor to use almost identical code block on playOnce
        if (!animation) {
            animation = this.getAnimations()[0];
        }

        switch (typeof (animation)) {
            case "string":
                animation = this.getFirstSpine().spineData.animations.find((x) => x.name === animation);
                break;
            case "number":
                if (animation >= 0 && animation < this.getAnimations().length) {
                    animation = this.getAnimations()[animation];
                } else {
                    throw new Error(`Invalid animation index given for ${this.name} (${animation})`);
                }
                break;
        }

        this.onStart.dispatch();

        if (this.playing) {
            if (this.animation.name === animation.name) {
                return;
            } else {
                this.stop();
            }
        }

        this.animation = animation;

        const duration = (animation.duration * 1000) / timeScale;

        this.playTimeout = Timer.setTimeout(() => {
            this.stop();
            this.onComplete.dispatch();
            this.play(animation);
        }, duration);

        this.setupPlay(true, timeScale);
    }

    public stop(hide: boolean = true) {
        if (this.playTimeout) {
            Timer.clearTimeout(this.playTimeout);
            this.playTimeout = undefined;
        }

        if (hide) {
            this.spineVisible = false;
            this.updateSpineVisibility();
        }

        if (this.playing) {
            for (const key in this.spines) {
                if (this.spines.hasOwnProperty(key)) {
                    this.spines[key].state.tracks.forEach((track) => track.time = 0);
                    this.spines[key].state.clearListeners();
                    this.spines[key].state.setEmptyAnimation(0, 0);
                    this.playing = false;
                    this.looping = false;
                    this.animation = undefined;
                    if (this.playContract) {
                        this.playContract.forceResolve();
                        this.playContract = undefined;
                    }
                }
            }
        }
    }

    public getAnimations(): spine.core.Animation[] {
        return this.getFirstSpine().spineData.animations;
    }

    public getAnimationIndex(): number {
        return this.getAnimations().indexOf(this.animation);
    }

    public isLooping(): boolean {
        return this.looping;
    }

    public setupSpine() {
        if (this.initialSpineSetup) {
            this.createSpine(Orientation.LANDSCAPE, this.spinePosition.landscape);
            this.createSpine(Orientation.PORTRAIT, this.spinePosition.portrait);

            this.updateSpineVisibility();

            this.initialSpineSetup = false;
        }
    }

    public getSpinePosition(): DualPosition {
        return this.spinePosition;
    }

    public setSlotVisible(slotKey: string, visible: boolean) {
        for (const key in this.spines) {
            if (this.spines.hasOwnProperty(key)) {
                const slot = this.spines[key].skeleton.findSlot(slotKey);
                if (slot) {
                    for (const spriteName in slot.sprites) {
                        const sprite = slot.sprites[spriteName];
                        sprite.visible = visible;
                    }
                }
            }
        }
    }

    public setAttachment(slot: string, attachment: string) {
        this.attachments.set(slot, attachment);

        this.updateAttachments();
    }

    private createSpine(orientation: Orientation, position: Position) {
        if (!this.spines[orientation] && !position.unavailable) {
            this.spines[orientation] = graphicsManager.createSpine(this.spineId);

            let bounds = this.spines[orientation].getBounds();

            // sometimes spines have no width and height on default skeleton, so temporarily play the anim to get the correct bounds
            if (bounds.width === 0 && bounds.height === 0) {
                const animName = this.spines[orientation].spineData.animations[0].name;
                this.spines[orientation].state.setAnimation(0, animName, true);
                this.spines[orientation].state.tracks[0].time = 10; // jump 10ms into the anim because the bounds can still be 0,0 at frame 0

                bounds = this.spines[orientation].getBounds();
            }

            this.spines[orientation].pivot.x = bounds.x;
            this.spines[orientation].pivot.y = bounds.y;

            this.addChild(this.spines[orientation]);

            this.spines[orientation].width = position.width;
            this.spines[orientation].height = position.height;

            this.spines[orientation].state.setEmptyAnimation(0, 0);
        }
    }

    private setupPlay(looping: boolean, timeScale = 1) {
        for (const key in this.spines) {
            if (this.spines.hasOwnProperty(key)) {

                this.spines[key].stateData.defaultMix = 0;
                this.spines[key].state.setEmptyAnimation(0, 0);
                this.spines[key].skeleton.setToSetupPose();
                this.spines[key].skeleton.setSkinByName("default");

                this.spines[key].state.setAnimation(0, this.animation.name, false);
                this.spines[key].state.tracks[0].time = 0;
                this.spines[key].state.timeScale = gameLoop.speed * timeScale;
                this.spines[key].state.clearListeners();
                this.spines[key].state.addListener({ event: (entry, event) => this.onSpineEvent(entry, event) });

                this.playing = true;
                this.looping = looping;
                this.spineVisible = true;

                this.updateSpineVisibility();
            }
        }

        this.updateAttachments();
    }

    private updateAttachments() {
        for (const key in this.spines) {
            if (this.spines.hasOwnProperty(key)) {
                this.attachments.forEach((attachment: string, slot: string) => {
                    if (this.spines[key].skeleton.findSlot(slot)) {
                        this.spines[key].skeleton.setAttachment(slot, attachment);
                    }
                });
            }
        }
    }

    private onSpineEvent(entry: spine.core.TrackEntry, event: spine.core.Event) {
        if (event?.data?.name) {
            Services.sound.event(SoundEvent.spine_NAME, event.data.name);
        }

        this.onEvent.dispatch(entry, event);
    }

    private updateSpineVisibility(): void {
        for (const key in this.spines) {
            if (this.spines.hasOwnProperty(key)) {
                this.spines[key].alpha = (this.spineVisible && key === canvasManager.orientation) ? 1 : 0;
            }
        }
    }

    private getFirstSpine() {
        return this.spines[Orientation.LANDSCAPE] || this.spines[Orientation.PORTRAIT];
    }
}
